/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";
import {
  getServiceEndpointDecoder,
  getServiceEndpointEncoder,
  getVerificationMethodDecoder,
  getVerificationMethodEncoder,
  type ServiceEndpoint,
  type ServiceEndpointArgs,
  type VerificationMethod,
  type VerificationMethodArgs,
} from "../types";

export const DID_DOCUMENT_DISCRIMINATOR = new Uint8Array([
  109, 220, 168, 224, 33, 173, 192, 166,
]);

export function getDidDocumentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    DID_DOCUMENT_DISCRIMINATOR,
  );
}

export type DidDocument = {
  discriminator: ReadonlyUint8Array;
  /** The DID string (e.g., "did:sol:devnet:HN7cABqLq46Es1jh92dQQisAq662SmxELLLsHHe4YWrH") */
  did: string;
  /** Controller of the DID (can update the document) */
  controller: Address;
  /** Verification methods (public keys) */
  verificationMethods: Array<VerificationMethod>;
  /** Service endpoints */
  serviceEndpoints: Array<ServiceEndpoint>;
  /** Context URIs (for W3C compatibility) */
  context: Array<string>;
  /** Also known as (alternative DIDs) */
  alsoKnownAs: Array<string>;
  /** DID document creation timestamp */
  createdAt: bigint;
  /** Last update timestamp */
  updatedAt: bigint;
  /** Version number (incremented on each update) */
  version: number;
  /** Deactivated flag */
  deactivated: boolean;
  /** Deactivation timestamp (if deactivated) */
  deactivatedAt: Option<bigint>;
  /** PDA bump */
  bump: number;
};

export type DidDocumentArgs = {
  /** The DID string (e.g., "did:sol:devnet:HN7cABqLq46Es1jh92dQQisAq662SmxELLLsHHe4YWrH") */
  did: string;
  /** Controller of the DID (can update the document) */
  controller: Address;
  /** Verification methods (public keys) */
  verificationMethods: Array<VerificationMethodArgs>;
  /** Service endpoints */
  serviceEndpoints: Array<ServiceEndpointArgs>;
  /** Context URIs (for W3C compatibility) */
  context: Array<string>;
  /** Also known as (alternative DIDs) */
  alsoKnownAs: Array<string>;
  /** DID document creation timestamp */
  createdAt: number | bigint;
  /** Last update timestamp */
  updatedAt: number | bigint;
  /** Version number (incremented on each update) */
  version: number;
  /** Deactivated flag */
  deactivated: boolean;
  /** Deactivation timestamp (if deactivated) */
  deactivatedAt: OptionOrNullable<number | bigint>;
  /** PDA bump */
  bump: number;
};

/** Gets the encoder for {@link DidDocumentArgs} account data. */
export function getDidDocumentEncoder(): Encoder<DidDocumentArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["did", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["controller", getAddressEncoder()],
      ["verificationMethods", getArrayEncoder(getVerificationMethodEncoder())],
      ["serviceEndpoints", getArrayEncoder(getServiceEndpointEncoder())],
      [
        "context",
        getArrayEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      [
        "alsoKnownAs",
        getArrayEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["createdAt", getI64Encoder()],
      ["updatedAt", getI64Encoder()],
      ["version", getU32Encoder()],
      ["deactivated", getBooleanEncoder()],
      ["deactivatedAt", getOptionEncoder(getI64Encoder())],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({ ...value, discriminator: DID_DOCUMENT_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link DidDocument} account data. */
export function getDidDocumentDecoder(): Decoder<DidDocument> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["did", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["controller", getAddressDecoder()],
    ["verificationMethods", getArrayDecoder(getVerificationMethodDecoder())],
    ["serviceEndpoints", getArrayDecoder(getServiceEndpointDecoder())],
    [
      "context",
      getArrayDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    [
      "alsoKnownAs",
      getArrayDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["createdAt", getI64Decoder()],
    ["updatedAt", getI64Decoder()],
    ["version", getU32Decoder()],
    ["deactivated", getBooleanDecoder()],
    ["deactivatedAt", getOptionDecoder(getI64Decoder())],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link DidDocument} account data. */
export function getDidDocumentCodec(): Codec<DidDocumentArgs, DidDocument> {
  return combineCodec(getDidDocumentEncoder(), getDidDocumentDecoder());
}

export function decodeDidDocument<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<DidDocument, TAddress>;
export function decodeDidDocument<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<DidDocument, TAddress>;
export function decodeDidDocument<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<DidDocument, TAddress> | MaybeAccount<DidDocument, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getDidDocumentDecoder(),
  );
}

export async function fetchDidDocument<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<DidDocument, TAddress>> {
  const maybeAccount = await fetchMaybeDidDocument(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeDidDocument<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<DidDocument, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeDidDocument(maybeAccount);
}

export async function fetchAllDidDocument(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<DidDocument>[]> {
  const maybeAccounts = await fetchAllMaybeDidDocument(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeDidDocument(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<DidDocument>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeDidDocument(maybeAccount));
}
